<script lang="ts">
  import {
    createCheckoutSession,
    createSetupIntent,
    defaultElementsOptions,
    stripeClient,
    updateCustomer,
  } from '$lib/gateway/stripeClient';
  import { userStore } from '$lib/stores/userStore';
  import { MD5 } from '$lib/helpers/cryptoHelpers';
  import { currencyFormatter } from '$lib/helpers/stringFormatters';
  import Button from '$lib/components/control/Button.svelte';
  import { BUTTON_SIZE, BUTTON_VARIANT } from '$lib/constants/constants';
  import type Stripe from 'stripe';
  import type { CheckoutData } from '../../../routes/api/stripe/checkout/+server';
  import _ from 'lodash';
  import SlidingModal from '$lib/components/website/SlidingModal.svelte';
  import AlertCircle from '../../../assets/svgs/alert-circle.svelte';
  import { page } from '$app/stores';
  import { billingSummaryText, periodicTotalDescription } from '$lib/helpers/billingTextHelpers';
  import { couponDescription, validCoupon } from '$lib/helpers/couponHelpers';
  import { intervalFormatter } from '$lib/helpers/stringFormatters';

  export let data;
  export let price;
  export let coupon: Stripe.Coupon | undefined = undefined;
  export let country: string | undefined = undefined;
  let abortController = new AbortController();
  let elements;
  let address: Stripe.Address;
  let addressElementLoading = true;
  let addressComplete = false;
  let paymentElementLoading = true;
  let paymentComplete = false;
  let checkoutDataLoading = false;
  let checkoutData: CheckoutData | undefined = undefined;
  let afterTrialData: CheckoutData;
  let checkoutLoading = false;
  let genericError: string | undefined;
  let couponError: string | undefined;
  let isValidCoupon = false;

  $: checkoutError = $page.url.searchParams.has('error');

  function closeErrorModal() {
    checkoutError = false;
    window.location.href = window.location.pathname;
  }

  function goToSupport() {
    window.location.href = 'https://support.sweat.com';
  }

  // User
  $: user = $userStore.user;

  // Create setup intent on load
  $: if (stripeClient) {
    initializeSetupIntent(user);
  }

  async function initializeSetupIntent(_) {
    if (!stripeClient) return;
    const hashedEmail = MD5(user?.email || '');
    const { clientSecret, defaultValues } = await createSetupIntent(
      `${data?.pageData?.sys.id}-${hashedEmail}`,
    );
    await createStripeElements(clientSecret, defaultValues);
  }

  let debouncedUpdateAddress = _.debounce(async (event) => {
    address = event.value.address;
    country = event.value.address.country;
    await updateCustomer(event.value);
    addressComplete =
      event.complete || (event.value.address.state && event.value.address.postal_code);
  }, 600);

  async function createStripeElements(clientSecret: string, defaultValues) {
    if (!stripeClient) return;
    addressComplete = false;

    // For Playwright testing purposes
    const options =
      process.env.NODE_ENV === 'test'
        ? {
            mode: 'subscription',
            amount: 0,
            currency: 'usd',
          }
        : {
            clientSecret,
          };

    elements = stripeClient.elements({
      ...options,
      ...defaultElementsOptions,
    });

    const addressElement = elements.create('address', {
      mode: 'billing',
      defaultValues: {
        name: defaultValues?.name,
        address: defaultValues?.address || { country },
      },
    });
    await addressElement.mount('#address-element');

    addressElement.on('loaderstart', () => {
      addressElementLoading = false;
    });

    addressElement.on('change', async (event) => {
      debouncedUpdateAddress(event);
    });

    const paymentElement = elements.create('payment', {
      layout: {
        type: 'tabs',
      },
    });
    await paymentElement.mount('#payment-element');

    paymentElement.on('loaderstart', () => {
      paymentElementLoading = false;
    });

    paymentElement.on('change', (event) => {
      paymentComplete = event.complete;
    });
  }

  $: isValidCoupon = validCoupon(coupon, price);

  // Create checkout session when price, user and address change
  $: if (price && user) {
    couponError = undefined;
    if (coupon && !isValidCoupon) {
      couponError = `This coupon can't be applied to the ${price.label} plan. Please select a different plan.`;
      checkoutData = undefined;
      checkoutDataLoading = false;
    } else {
      handleCheckoutSession(price, user, address, coupon).catch((err) => {
        if (err.name === 'AbortError') return;
        console.error(err);
        genericError = 'Something went wrong. Please try again or contact support.';
        checkoutDataLoading = false;
      });
    }
  }

  async function handleCheckoutSession(price, _, __, ___) {
    if (!stripeClient || !price.id) return;
    abortController.abort();
    abortController = new AbortController();
    checkoutDataLoading = true;
    genericError = undefined;
    const res = await createCheckoutSession(
      {
        joinPageId: data?.pageData?.sys.id,
        priceId: price.id,
        couponId: coupon?.id,
        currency: price.currency,
        isPreview: data.isPreview,
        trialPeriodDays:
          price?.trialPeriodDays > 0 ? price.trialPeriodDays : data?.pageData?.trialPeriodDays,
      },
      abortController,
    );
    checkoutData = res?.checkoutData;
    afterTrialData = res?.afterTrialData;

    checkoutDataLoading = false;
  }

  async function handleCheckoutSubmit() {
    checkoutLoading = true;

    // For Playwright testing purposes
    if (process.env.NODE_ENV === 'test') {
      const params = new URLSearchParams({
        setup_intent: 'test_setup_intent_id',
        redirect_status: 'succeeded',
      });
      return window.location.replace(
        `${window.location.origin}/api/stripe/checkout/callback?${params}`,
      );
    }

    const result = await stripeClient.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/api/stripe/checkout/callback`,
      },
    });

    checkoutLoading = false;
    if (result.error) {
      genericError = result.error.message;
    }
  }

  // Either summarize the after trial data if it's available (if there's a free trial), or the checkout data
  // This precludes displaying three stages of summary (trial, discounted, full price) - we might need to change this if that ever becomes a requirement
  $: displaySummaryData = afterTrialData || checkoutData;

  $: currency = checkoutData?.currency;
  $: checkoutDisabled =
    !!genericError ||
    !user ||
    !addressComplete ||
    !paymentComplete ||
    checkoutLoading ||
    !isValidCoupon;

  // handlePlausible is used to track address and payment selection on click
  $: plausibleAdd = false;
  $: plausiblePay = false;
  function handlePlausibleAdd() {
    plausibleAdd = !plausibleAdd;
  }
  function handlePlausiblePay() {
    plausiblePay = !plausiblePay;
  }
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div on:click={handlePlausibleAdd}>
  <p class="text-sm font-bold mb-4">3. Add your billing address</p>
  {#if addressElementLoading}
    <div class="h-24 rounded-sm bg-gray-200 animate-pulse" />
  {/if}
  <div
    id="address-element"
    class:plausible-event-name--new_paywall_add_billing_address={plausibleAdd}
  ></div>
</div>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div on:click={handlePlausiblePay}>
  <p class="text-sm font-bold mb-4">4. Your payment details</p>
  {#if paymentElementLoading}
    <div class="h-24 rounded-sm bg-gray-200 animate-pulse" />
  {/if}
  <div id="payment-element" class:plausible-event-name--new_paywall_select_pay={plausiblePay}></div>
</div>

{#if couponError}
  <div class="flex flex-col items-start gap-4 p-4 my-4 bg-error-100 text-black rounded-sm">
    <p>
      {couponError}
    </p>
  </div>
{/if}

{#if !couponError && genericError}
  <div class="flex flex-col items-start gap-4 p-4 my-4 bg-error-100 text-black rounded-sm">
    <p>
      {genericError}
    </p>
    <Button
      text="Contact support"
      variant={BUTTON_VARIANT.OUTLINED}
      buttonSize={BUTTON_SIZE.CUSTOM}
      customClass="px-6 py-2.5"
      onClick={goToSupport}
    />
  </div>
{/if}

{#if !couponError && user}
  {#if checkoutDataLoading}
    <div class="h-64 rounded-sm bg-gray-200 animate-pulse" />
  {:else}
    <div class="flex flex-col gap-2">
      {#if checkoutData?.trialPeriodDays > 0}
        <div class="flex justify-between items-baseline">
          <span
            class="text-xs uppercase text-white font-bold bg-primary-500 px-3 py-0.5 rounded-sm w-fit mr-1 -mt-2 whitespace-nowrap text-center leading-4"
            >Free Trial</span
          >
          <span class="leading-4 text-right text-pretty"
            >Nothing to pay until {checkoutData?.billingStartDate}</span
          >
        </div>
        <div class="border border-gray-500 w-full mt-2 mb-4" />

        <div class="flex justify-between items-center">
          <span
            class="text-xs uppercase text-white font-bold bg-fuchsia-950 px-3 py-0.5 rounded-sm w-fit mb-1 -mt-2 whitespace-nowrap text-center"
            >After your trial</span
          >
        </div>
      {/if}

      {#if displaySummaryData}
        <div class="flex justify-between items-center">
          <span>
            Subscription
            {#if displaySummaryData?.tax > 0 && displaySummaryData?.tax_calculation === 'inclusive'}
              <span class="text-gray-400 text-xs"> Before tax</span>
            {/if}
          </span>
          <span>{currencyFormatter(displaySummaryData?.subtotal, currency)}</span>
        </div>

        {#if displaySummaryData?.tax > 0}
          <div class="flex justify-between items-center">
            <span>
              {displaySummaryData?.tax_name}
              {#if displaySummaryData?.tax_percentage > 0}
                <span class="text-gray-400 text-xs"> {displaySummaryData?.tax_percentage}%</span>
              {/if}
            </span>
            <span>{currencyFormatter(displaySummaryData?.tax, currency)}</span>
          </div>
        {/if}

        {#if displaySummaryData?.discount}
          <div class="flex justify-between items-center text-pretty">
            <span
              ><strong>{coupon ? coupon.code : 'Promotion'}</strong>
              {couponDescription(
                coupon,
                displaySummaryData?.interval,
                displaySummaryData?.intervalCount,
                currency,
              )}</span
            >
            <span class="text-sweat-pink-50 text-right text-pretty"
              >You save {currencyFormatter(displaySummaryData?.discount, currency)}</span
            >
          </div>
        {/if}

        <div class="border border-gray-500 w-full my-2" />

        <div
          class="flex justify-between items-center font-bold text-display-xxs md:text-display-xs"
        >
          <span>{periodicTotalDescription(checkoutData)}</span>
          <span data-testid="checkout-total"
            >{currencyFormatter(displaySummaryData?.total, currency)}</span
          >
        </div>
      {/if}

      {#if checkoutData?.interval && checkoutData?.intervalCount && checkoutData?.billingStartDate}
        <span class="text-gray-400 text-sm mt-2">
          {billingSummaryText(checkoutData)}
        </span>
      {/if}
    </div>

    <div>
      <Button
        dataTestId="checkout-submit"
        text={checkoutData?.trialPeriodDays > 0 ? 'Start free trial' : 'Start your subscription'}
        variant={BUTTON_VARIANT.PRIMARY}
        buttonSize={BUTTON_SIZE.LARGE}
        customClass="w-full max-w-[343px]"
        plausibleClass="pay"
        disabled={checkoutDisabled}
        onClick={handleCheckoutSubmit}
      />
    </div>
  {/if}
{/if}

<SlidingModal bind:showModal={checkoutError} onClose={closeErrorModal}>
  <div class="flex flex-col items-center gap-4">
    <AlertCircle customClass="w-16 h-16 text-error-500" />
    <p class="lg:text-display-md font-bold text-lg">Something went wrong</p>
    <p class="text-xl text-center text-gray-500 text-sm">
      We couldn't process your payment. Please try again or contact support.
    </p>

    <Button
      text="Contact support"
      variant={BUTTON_VARIANT.OUTLINED}
      buttonSize={BUTTON_SIZE.LARGE}
      customClass="mt-4"
      onClick={goToSupport}
    />
  </div>
</SlidingModal>
